import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { api } from '../utils/api';
import { sortEmployeesByDepartmentAndId } from '../utils/helpers';
import { Scanner } from '@yudiel/react-qr-scanner';
import { 
  ClipboardCheck, Send, Loader2, AlertCircle, CheckCircle, 
  Info, Car, Search, X, QrCode, RefreshCw, Clock, MapPin 
} from 'lucide-react';

// --- Helper Functions ---
const formatCountdown = (seconds) => {
  if (seconds <= 0) return "00:00";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

// --- RequestItem Component ---
const RequestItem = ({ request, currentUser, onReportBack, onQuickGPS, onCancel }) => {
  const isRequester = request.requester_employee_id === currentUser.employee_id;
  
  const [remainingTime, setRemainingTime] = useState(null); 
  
  const expirationTime = useMemo(() => {
    const requestTime = new Date(request.request_time);
    requestTime.setMinutes(requestTime.getMinutes() + 10);
    return requestTime;
  }, [request.request_time]);

  useEffect(() => {
    if (request.status !== 'active' && request.status !== 'no_return_required') {
      setRemainingTime(0);
      return;
    }

    let timer; 
    const updateRemainingTime = () => {
      const now = new Date();
      const secondsLeft = Math.round((expirationTime.getTime() - now.getTime()) / 1000);
      
      if (secondsLeft <= 0) {
        setRemainingTime(0);
        clearInterval(timer); 
      } else {
        setRemainingTime(secondsLeft);
      }
    };

    updateRemainingTime();
    timer = setInterval(updateRemainingTime, 1000); 
    return () => clearInterval(timer);
  }, [expirationTime, request.status]);

  const canCancel = remainingTime > 0 && isRequester;
  const canReportBack = request.status === 'active' && isRequester;

  const getStatusChip = () => {
    switch (request.status) {
      case 'active':
        return <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">รอกลับ (ก่อน {new Date(request.expected_return_time).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })} น.)</span>;
      case 'returned':
        return <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded-full">กลับมาแล้ว ({new Date(request.actual_return_time).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })} น.)</span>;
      case 'no_return_required':
        return <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">อนุมัติออก (ไม่ต้องแจ้งกลับ)</span>;
      case 'cancelled':
        return <span className="px-2 py-0.5 bg-gray-100 text-gray-800 text-xs font-medium rounded-full">ยกเลิกแล้ว</span>;
      default:
        return null;
    }
  };

  const displayDate = new Date(request.departure_time || request.request_time);

  const requesterFullName = `${request.requester_first_name || ''} ${request.requester_last_name || ''}`.trim();
  
  const participants = (request.group_members_names || '')
    .split(', ')
    .filter(name => name && name.trim() !== requesterFullName)
    .join(', ');

  return (
    <div className="p-4 border border-gray-200 rounded-lg bg-white hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start">
        <div>
          <span className="font-semibold text-gray-800">
            {displayDate.toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' })}
          </span>
          <span className="text-gray-600 ml-2">
            ( {displayDate.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })} น.)
          </span>
        </div>
        {getStatusChip()}
      </div>
      
      <p className="text-sm text-gray-700 mt-3"><span className="font-medium">เหตุผล:</span> {request.reason}</p>
      {request.license_plate && (
        <p className="text-sm text-gray-700 mt-1"><span className="font-medium">ทะเบียนรถ:</span> {request.license_plate}</p>
      )}
      
      <div className="bg-gray-50 p-3 rounded-md mt-3 border border-gray-100">
        <p className="text-sm text-gray-700">
          <span className="font-medium text-blue-700">ผู้ยื่นเรื่อง:</span> {requesterFullName || '-'}
        </p>
        <p className="text-sm text-gray-700 mt-1">
          <span className="font-medium text-gray-600">ผู้ร่วมทาง:</span> {participants || '-'}
        </p>
      </div>
      
      {(canReportBack || canCancel) && (
        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
          {canReportBack && (
            <>
              {/* ปุ่ม 1: สแกน QR (ปุ่มหลัก) */}
              <button
                onClick={() => onReportBack(request.id)}
                className="flex-1 bg-blue-600 text-white py-2 px-3 rounded-md hover:bg-blue-700 flex items-center justify-center space-x-2 text-sm transition-colors shadow-sm"
              >
                <QrCode className="h-4 w-4" />
                <span>แจ้งกลับ (QR Code)</span>
              </button>

              {/* ปุ่ม 2: แจ้งพิกัด GPS (ปุ่มรอง) */}
              <button
                onClick={() => onQuickGPS(request.id)}
                className="flex-1 bg-indigo-50 text-indigo-700 py-2 px-3 rounded-md hover:bg-indigo-100 flex items-center justify-center space-x-2 text-sm transition-colors border border-indigo-200"
              >
                <MapPin className="h-4 w-4" />
                <span>แจ้งกลับ (GPS)</span>
              </button>
            </>
          )}
          
          {canCancel && (
            <button
              onClick={() => onCancel(request.id)}
              className="flex-none bg-red-50 text-red-600 py-2 px-3 rounded-md hover:bg-red-100 border border-red-200 flex items-center justify-center space-x-1 text-sm transition-colors"
              title="ยกเลิกคำขอ"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// --- Main Component ---
const PermissionLeaveForm = ({ currentUser }) => {
  // State
  const [reason, setReason] = useState('');
  const [licensePlate, setLicensePlate] = useState('');
  const [departureTime, setDepartureTime] = useState(new Date().toTimeString().slice(0, 5));
  const [selectedMembers, setSelectedMembers] = useState([]);
  
  const [employeeList, setEmployeeList] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  
  const [message, setMessage] = useState({ type: '', text: '' });

  // QR Scanner State
  const [showScanner, setShowScanner] = useState(false);
  const [scanTargetRequestId, setScanTargetRequestId] = useState(null);
  
  // Fetch Data
  const fetchData = async () => {
    setLoadingEmployees(true);
    setLoadingRequests(true);
    try {
      const employees = await api.getEmployees();
      const filteredEmployees = employees.filter(emp => 
        emp.is_active && 
        emp.employee_id !== currentUser.employee_id &&
        emp.position !== 'ผู้ดูแลระบบ' 
      );
      
      const sortedEmployees = sortEmployeesByDepartmentAndId(filteredEmployees);
      setEmployeeList(sortedEmployees);
      
      const requests = await api.getMyPermissionRequests(currentUser.employee_id);
      setMyRequests(requests);
    } catch (error) {
      console.error("Error fetching data:", error);
      setMessage({ type: 'error', text: 'ไม่สามารถโหลดข้อมูลพนักงานหรือคำขอได้' });
    } finally {
      setLoadingEmployees(false);
      setLoadingRequests(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [currentUser]);

  const handleMemberToggle = (employeeId) => {
    setSelectedMembers((prevSelected) => {
      if (prevSelected.includes(employeeId)) {
        return prevSelected.filter(id => id !== employeeId);
      } else {
        return [...prevSelected, employeeId];
      }
    });
  };
  
  const filteredEmployeeList = useMemo(() => {
    if (!searchTerm) return employeeList;
    const lowerCaseSearch = searchTerm.toLowerCase();
    return employeeList.filter(emp => 
      emp.first_name.toLowerCase().includes(lowerCaseSearch) ||
      emp.last_name.toLowerCase().includes(lowerCaseSearch) ||
      (emp.department || '').toLowerCase().includes(lowerCaseSearch) ||
      (emp.employee_id || '').toLowerCase().includes(lowerCaseSearch)
    );
  }, [searchTerm, employeeList]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!reason.trim() || !departureTime) {
      setMessage({ type: 'error', text: 'กรุณาระบุเหตุผลและเวลาที่ขออนุญาตออก' });
      return;
    }

    setSubmitting(true);
    setMessage({ type: '', text: '' });

    const selectedHour = parseInt(departureTime.split(':')[0], 10);

    if (selectedHour >= 14) {
      const confirmed = window.confirm(
        'คุณกำลังขออนุญาตออกตั้งแต่ 14:00 น. เป็นต้นไป\n\nระบบจะบันทึกสถานะของคุณเป็น "ขออนุญาตออก" และไม่ต้องแจ้งกลับ\n\nยืนยันการดำเนินการหรือไม่?'
      );
      if (!confirmed) {
        setSubmitting(false);
        return;
      }
    }

    try {
      const requestData = {
        employee_id: currentUser.employee_id,
        reason: reason,
        license_plate: licensePlate,
        group_members: selectedMembers,
        departure_time: departureTime 
      };

      const response = await api.submitPermissionRequest(requestData);
      
      if (response.success) {
        setMessage({ type: 'success', text: response.message });
        setReason('');
        setLicensePlate('');
        setSelectedMembers([]); 
        setDepartureTime(new Date().toTimeString().slice(0, 5)); 
        setSearchTerm(''); 
        
        const requests = await api.getMyPermissionRequests(currentUser.employee_id);
        setMyRequests(requests);
      } else {
        setMessage({ type: 'error', text: response.error || 'ไม่สามารถยื่นคำขอได้' });
      }
    } catch (error) {
      console.error("Error submitting request:", error);
      setMessage({ type: 'error', text: 'เกิดข้อผิดพลาด: ' + error.message });
    } finally {
      setSubmitting(false);
    }
  };
  
  // 1. กดปุ่มสแกน QR
  const handleReportBackClick = (requestId) => {
    setScanTargetRequestId(requestId);
    setShowScanner(true);
    setMessage({ type: '', text: '' });
  };

  // 2. กดปุ่ม GPS ด่วน (หน้าแรก)
  const handleQuickGPS = (requestId) => {
    setScanTargetRequestId(requestId);
    handleGPSCheckIn(requestId); 
  };

  // Logic รับค่าจาก Scanner
  const handleQrScan = async (detectedCodes) => {
    if (detectedCodes && detectedCodes.length > 0) {
      const scannedText = detectedCodes[0].rawValue;
      
      if (scannedText && scannedText.startsWith("RETURN|")) {
        const parts = scannedText.split('|');
        
        if (parts.length === 3) {
            const scannedRequestId = parseInt(parts[1]);
            const scannedEmployeeId = parts[2];

            if (scannedRequestId !== scanTargetRequestId) {
                alert("❌ QR Code ไม่ถูกต้อง (ผิดรายการ)");
                return; 
            }

            if (scannedEmployeeId !== currentUser.employee_id) {
                alert("❌ ไม่ใช่รายการของคุณ");
                return;
            }

            setShowScanner(false); 
            await submitReturn(scanTargetRequestId, 'qr', scannedText);
        }
      }
    }
  };

  // Logic ดึงพิกัด GPS
  const handleGPSCheckIn = (requestIdOverride = null) => {
    const targetId = requestIdOverride || scanTargetRequestId;
    
    if (!navigator.geolocation) {
      alert("เบราว์เซอร์ของคุณไม่รองรับการระบุตำแหน่ง");
      return;
    }

    setGpsLoading(true);
    if (requestIdOverride) setMessage({ type: 'info', text: 'กำลังระบุตำแหน่ง GPS...' });

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        const locationData = { latitude, longitude, accuracy };
        
        setShowScanner(false);
        submitReturn(targetId, 'gps', locationData);
        setGpsLoading(false);
      },
      (error) => {
        console.error("GPS Error:", error);
        alert("ไม่สามารถระบุตำแหน่งได้: " + error.message);
        setMessage({ type: 'error', text: 'ไม่สามารถระบุตำแหน่ง GPS ได้' });
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // ส่งข้อมูลแจ้งกลับ
  const submitReturn = async (requestId, method, data) => {
    setSubmitting(true);
    
    try {
      let locationPayload;

      if (method === 'qr') {
        locationPayload = {
          source: 'qr_code',
          code_data: data
        };
      } else if (method === 'gps') {
        locationPayload = {
          source: 'gps',
          latitude: data.latitude,
          longitude: data.longitude,
          accuracy: data.accuracy
        };
      } else {
        locationPayload = { source: 'manual' };
      }

      const returnData = {
        request_id: requestId,
        requester_employee_id: currentUser.employee_id,
        location: locationPayload
      };
      
      const response = await api.reportBackPermission(returnData);
      
      if (response.success) {
        const methodText = method === 'qr' ? 'สแกน QR Code' : 'พิกัด GPS';
        setMessage({ type: 'success', text: `แจ้งกลับเรียบร้อยแล้ว! (${methodText})` });
        const requests = await api.getMyPermissionRequests(currentUser.employee_id);
        setMyRequests(requests);
      } else {
         setMessage({ type: 'error', text: `ไม่สามารถแจ้งกลับได้: ${response.error}` });
      }
      
    } catch (error) {
      console.error("Error reporting back:", error);
      setMessage({ type: 'error', text: `เกิดข้อผิดพลาด: ${error.message}` });
    } finally {
      setSubmitting(false);
      setScanTargetRequestId(null);
    }
  };
  
  const handleCancel = async (requestId) => {
    if (!window.confirm('คุณต้องการยกเลิกคำขอนี้หรือไม่?\n(สามารถยกเลิกได้ภายใน 10 นาทีหลังยื่นเรื่อง)')) {
      return;
    }
    
    setSubmitting(true);
    setMessage({ type: '', text: '' });
    
    try {
      const cancelData = {
        request_id: requestId,
        requester_employee_id: currentUser.employee_id
      };
      
      const response = await api.cancelPermissionRequest(cancelData);
      
      if (response.success) {
        setMessage({ type: 'success', text: 'ยกเลิกคำขอเรียบร้อยแล้ว' });
        const requests = await api.getMyPermissionRequests(currentUser.employee_id);
        setMyRequests(requests);
      } else {
         setMessage({ type: 'error', text: `ไม่สามารถยกเลิกได้: ${response.error}` });
      }
      
    } catch (error) {
       console.error("Error canceling request:", error);
       setMessage({ type: 'error', text: `เกิดข้อผิดพลาด: ${error.message}` });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-8 relative">
      
      {/* Modal สำหรับ Scan QR Code */}
      {showScanner && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-xl max-w-md w-full overflow-hidden relative shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="p-4 bg-gray-50 flex justify-between items-center border-b">
              <h3 className="font-bold text-lg flex items-center text-gray-800">
                <QrCode className="w-5 h-5 mr-2 text-blue-600" /> 
                สแกนจุดแจ้งกลับ
              </h3>
              <button 
                onClick={() => { setShowScanner(false); setScanTargetRequestId(null); }}
                className="text-gray-400 hover:text-red-500 bg-white rounded-full p-1 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-4 bg-black relative">
              <div className="rounded-lg overflow-hidden relative h-64 bg-gray-900 mb-4 border-2 border-gray-700">
                 <Scanner
                    onScan={handleQrScan}
                    onError={(error) => console.log(error)}
                    components={{ audio: false, torch: true, countDown: false, scanner: true }}
                    styles={{ container: { width: '100%', height: '100%' }, video: { width: '100%', height: '100%', objectFit: 'cover' } }}
                />
                <div className="absolute bottom-3 left-0 right-0 text-center pointer-events-none">
                    <span className="bg-black/70 text-white px-4 py-1.5 rounded-full text-sm font-medium tracking-wide">
                        ส่องไปที่ QR Code บนหน้าจอ
                    </span>
                </div>
              </div>
              
              <div className="flex flex-col space-y-3">
                <button 
                    onClick={() => handleGPSCheckIn()}
                    disabled={gpsLoading}
                    className="w-full py-3.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium flex items-center justify-center transition-all shadow-lg hover:shadow-indigo-500/30"
                >
                    {gpsLoading ? (
                        <>
                            <Loader2 className="animate-spin h-5 w-5 mr-2" />
                            กำลังระบุพิกัด...
                        </>
                    ) : (
                        <>
                            <MapPin className="h-5 w-5 mr-2" />
                            เปลี่ยนไปใช้ GPS แทน
                        </>
                    )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          ขออนุญาตออกนอกสถานที่
        </h1>
        <p className="text-gray-600 mt-2">
          ยื่นคำขออนุญาตออกนอกสถานที่ และสแกน QR Code หรือใช้ GPS เพื่อแจ้งกลับ
        </p>
      </div>
      
      {/* Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      
        {/* --- คอลัมน์ซ้าย: ฟอร์มยื่นคำขอ --- */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <ClipboardCheck className="h-6 w-6 mr-2 text-blue-500" />
                แบบฟอร์มขออนุญาต
              </h2>
            </div>
            
            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* Info Box */}
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-100 text-blue-800 flex items-start">
                  <Info className="h-5 w-5 mr-3 flex-shrink-0 mt-0.5 text-blue-600" />
                  <div className="text-sm">
                    <strong className="block mb-1">โปรดทราบ:</strong>
                    <ul className="list-disc list-inside space-y-1 ml-1 text-blue-700">
                      <li>ออกก่อน 14:00 น. ต้องแจ้งกลับภายใน 2 ชม.</li>
                      <li>เมื่อกลับมาถึง ให้ <strong>สแกน QR Code</strong> หรือ <strong>กดแจ้งพิกัด GPS</strong></li>
                    </ul>
                  </div>
                </div>
                
                {/* เหตุผล */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    เหตุผลที่ขออนุญาต *
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                    placeholder="เช่น ติดต่อราชการ, ธุระส่วนตัว"
                    required
                    disabled={submitting}
                  />
                </div>
                
                {/* เวลา */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    เวลาที่ขออนุญาตออก *
                  </label>
                  <div className="relative">
                    <Clock className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="time"
                      value={departureTime}
                      onChange={(e) => setDepartureTime(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                      required
                      disabled={submitting}
                    />
                  </div>
                </div>
                
                {/* ทะเบียนรถ */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ป้ายทะเบียนรถ (ถ้ามี)
                  </label>
                  <div className="relative">
                    <Car className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={licensePlate}
                      onChange={(e) => setLicensePlate(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                      placeholder="เช่น 1กท 1234"
                      disabled={submitting}
                    />
                  </div>
                </div>
                
                {/* ผู้ร่วมทาง */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ผู้ที่ออกไปด้วย (ถ้ามี)
                  </label>
                  <div className="relative">
                    <Search className="h-5 w-5 text-gray-400 absolute left-3 top-4" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3 transition-shadow"
                      placeholder="ค้นหาชื่อ, แผนก หรือรหัสพนักงาน"
                      disabled={submitting}
                    />
                    
                    <div className="w-full border border-gray-200 rounded-md h-60 overflow-y-auto bg-gray-50/50">
                      {loadingEmployees ? (
                        <div className="text-center text-gray-500 py-8 text-sm flex flex-col items-center">
                          <Loader2 className="h-6 w-6 animate-spin text-blue-400 mb-2" />
                          กำลังโหลดรายชื่อ...
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {filteredEmployeeList.map(emp => (
                            <label 
                              key={emp.employee_id} 
                              className={`flex items-center space-x-3 p-3 cursor-pointer transition-colors ${selectedMembers.includes(emp.employee_id) ? 'bg-blue-50' : 'hover:bg-white'}`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedMembers.includes(emp.employee_id)}
                                onChange={() => handleMemberToggle(emp.employee_id)}
                                disabled={submitting}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0 cursor-pointer"
                              />
                              <div className="flex flex-col">
                                <span className="text-sm font-medium text-gray-900">
                                  {emp.first_name} {emp.last_name}
                                </span>
                                <span className="text-xs text-gray-500 mt-0.5">
                                  {emp.department || 'N/A'} (ID: {emp.employee_id})
                                </span>
                              </div>
                            </label>
                          ))}
                          
                          {searchTerm && filteredEmployeeList.length === 0 && (
                             <div className="text-center text-gray-500 py-8 text-sm bg-white">
                               ไม่พบรายชื่อที่ตรงกับ <span className="font-semibold">"{searchTerm}"</span>
                             </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Message */}
                {message.text && (
                  <div
                    className={`p-4 rounded-md flex items-center ${
                      message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 
                      (message.type === 'info' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-red-50 text-red-700 border border-red-200')
                    }`}
                  >
                    {message.type === 'success' ? <CheckCircle className="h-5 w-5 mr-3 flex-shrink-0" /> :
                     message.type === 'info' ? <Loader2 className="animate-spin h-5 w-5 mr-3 flex-shrink-0" /> :
                     <AlertCircle className="h-5 w-5 mr-3 flex-shrink-0" />}
                    <span className="text-sm font-medium">{message.text}</span>
                  </div>
                )}
                
                {/* Submit Button */}
                <div className="border-t pt-6">
                  <button
                    type="submit"
                    disabled={submitting || loadingEmployees}
                    className="w-full bg-blue-600 text-white py-3.5 px-6 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-lg font-bold transition-all shadow-md hover:shadow-lg"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="animate-spin h-5 w-5" />
                        <span>กำลังส่งข้อมูล...</span>
                      </>
                    ) : (
                      <>
                        <Send className="h-5 w-5" />
                        <span>ยื่นคำขออนุญาตออก</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        
        {/* --- คอลัมน์ขวา: ประวัติคำขอ --- */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <RefreshCw className="h-6 w-6 mr-2 text-blue-500" />
                ประวัติการขออนุญาต
              </h2>
              <button
                onClick={fetchData}
                disabled={loadingRequests || submitting}
                className="text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50 transition-colors flex items-center"
              >
                รีเฟรช
              </button>
            </div>
            
            <div className="p-6 space-y-4 max-h-[800px] overflow-y-auto bg-gray-50/30">
              {loadingRequests ? (
                <div className="text-center text-gray-500 py-12 flex flex-col items-center">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-400 mb-3" />
                  กำลังโหลดประวัติ...
                </div>
              ) : myRequests.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border border-dashed border-gray-300">
                  <ClipboardCheck className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 font-medium">ยังไม่มีประวัติการขออนุญาต</p>
                </div>
              ) : (
                myRequests.map(req => (
                  <RequestItem 
                    key={req.id} 
                    request={req} 
                    currentUser={currentUser}
                    onReportBack={handleReportBackClick} 
                    onQuickGPS={handleQuickGPS}          
                    onCancel={handleCancel}
                  />
                ))
              )}
            </div>
          </div>
        </div>
        
      </div>
    </div>
  );
};

export default PermissionLeaveForm;